import { jwtDecode } from "jwt-decode";

/**
 * @typedef PulseTagsRequiredOptions
 * @property {string} env - FIAAS environment
 * @property {string} brandName - the NMP brand name
 * @property {string} [applicationName] - name of the application
 */
/**
 * @typedef PulseTagLibraryOptions
 * @property {string|null} [spidId] - Schibsted ID
 * @property {string|null} [loginId] - Brand-specific login ID
 * @property {string|null} [pulseClientVersion] - Specific version of pulse-sdk script
 * @property {string|null} [pulseInitVersion] - Specific version of finn-pulse-init script
 **/
/**
 * @typedef {PulseTagsRequiredOptions & PulseTagLibraryOptions} PulseTagsOptions
 */

/**
 * @typedef PulseTags
 * @property {Array<string>} script - the script tag
 * @property {Array<string>} meta - meta tags used by tracking scripts
 */
/**
 * @param {PulseTagsOptions} options - middleware configuration options
 * @returns {PulseTags}
 */
export function createPulseTags({
	applicationName,
	spidId,
	loginId,
	brandName,
	pulseClientVersion,
	pulseInitVersion,
}) {
	const isAurora = brandName.toLowerCase() !== "finn";
	const meta = [
		`<meta name="nmp:tracking:aurora" content="${isAurora ? "1" : "0"}">`,
		`<meta name="nmp:tracking:brand" content="${brandName}">`,
		`<meta name="nmp:tracking:app-name" content="${applicationName}">`,
		`<meta property="mbl:login" content="${spidId ? "1" : "0"}">`,
	];
	if (spidId) {
		meta.push(
			`<meta name="nmp:tracking:spid-id" content="${spidId}">`,
			`<meta name="nmp:tracking:login-id" content="${loginId}">`,
		);
	}

	const scripts = [
		`<script type="text/javascript" async src="${urls.cookieCleanerUrl}"></script>`,
		// Use `defer` for Pulse scripts to get predictable execution order
		`<script type="text/javascript" defer src="${urls.pulseEventListenerUrl}"></script>`,
		`<script type="text/javascript" defer src="${urls.pulseUrl}"></script>`,
		`<script type="text/javascript" defer src="${urls.finnPulseInitUrl.replace(
			/<VERSION>/,
			versionPattern.test(pulseInitVersion)
				? pulseInitVersion
				: defaults.finnPulseInit,
		)}"></script>`,
		`<script type="text/javascript" defer src="${urls.finnPulseClientUrl.replace(
			/<VERSION>/,
			versionPattern.test(pulseClientVersion)
				? pulseClientVersion
				: defaults.finnPulseSdk,
		)}"></script>`,
	];
	if (!isAurora && brandName.toLowerCase() === "finn") {
		scripts.push(
			`<script type="text/javascript" async src="${urls.analyticsUrl}"></script>`,
		);
	}

	return {
		meta,
		script: scripts,
	};
}

/**
 * Function parses the JWT token and returns the SPID and the brand-specific login ID.
 *
 * @param {Object} obj - object structure to use from the response (Express) or reply (Fastify)
 * @return {{loginId, spidId}|null} the parsed values for login and spid
 * @deprecated Use {@link parseJwt} instead, which is not tied to an object structure.
 */
export function parseJwtToken(obj) {
	const hasFinnToken = obj.finnToken;
	const hasFinnTokenOnPodiumContext =
		obj.podium && obj.podium.context && obj.podium.context.finnToken;

	if (!hasFinnToken && !hasFinnTokenOnPodiumContext) {
		return null;
	}

	return parseJwt(hasFinnToken ? obj.finnToken : obj.podium.context.finnToken);
}

/**
 * Parses the JWT token and returns the SPID and the brand-specific login ID.
 *
 * @param {string} [token]
 * @return {{ loginId: string, spidId: string } | null} The parsed values for login and spid, or null if the token was missing or unparsable.
 * @example
 * ```js
 * // express-based
 * const loginJwt =
 *   res.locals.loginJwt ||
 *   res.locals.finnToken ||
 *   res.locals.podium?.context?.finnToken;
 * const result = parseJwt(loginJwt);
 * ```
 * @example
 * ```js
 * // fastify-based
 * const loginJwt =
 *   request.loginJwt ||
 *   reply.app.finnToken ||
 *   reply.app.podium?.context?.finnToken;
 * const result = parseJwt(loginJwt);
 * ```
 */
export function parseJwt(token) {
	if (!token) return null;
	try {
		const decodeResult = jwtDecode(token);
		return {
			// @ts-ignore
			spidId: decodeResult.spidId,
			// @ts-ignore
			loginId: decodeResult.loginId,
		};
		// eslint-disable-next-line no-unused-vars
	} catch (err) {
		return null;
	}
}

export const urls = {
	analyticsUrl:
		"https://assets.finn.no/pkg/@finn-no/analytics/v26/analytics.min.js",
	cookieCleanerUrl:
		"https://static.finncdn.no/_c/cookie-whitelist/v1.0.25/clean.min.js",
	pulseEventListenerUrl:
		"https://assets.finn.no/pkg/pulse-event-listener/v1/index.js",
	pulseUrl: "https://sdk-cdn.pulse.m10s.io/versioned/3/loader.min.js",
	finnPulseInitUrl:
		"https://assets.finn.no/pkg/@finn-no/finn-pulse-init/<VERSION>/finn-pulse-init.min.js",
	finnPulseClientUrl:
		"https://assets.finn.no/pkg/@finn-no/pulse-sdk/<VERSION>/client.min.js",
};

const defaults = {
	finnPulseSdk: "v0",
	finnPulseInit: "v1",
};

const versionPattern = /^\d+(\.\d+\.\d+)?(-next\.?\d+)?$/;
